import { Component, ElementRef, Input, SimpleChanges, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import QRCodeStyling from 'qr-code-styling-next';
import { PropertyListingGetPropertyDetailsResponseDTO } from 'src/app/real-estate/Interface & Service/property-listing/property-listing.interface';
import { AISearchQueryRequestDTO } from 'src/app/Common/AI_Service/AI.interface';
import { AIService } from 'src/app/Common/AI_Service/AI.service';



@Component({
  selector: 'app-share-listing',
  templateUrl: './share-listing.component.html',
  styleUrls: ['./share-listing.component.css']
})
export class ShareListingComponent {
  @Input({ required: true }) link!: string;
  @Input({ required: true }) header!: string;
  @Input({ required: true }) subHeader!: string;
  @Input({ required: true }) access: 'admin' | 'user' = 'admin';
  @Input({ required: true }) propertyData!: PropertyListingGetPropertyDetailsResponseDTO;
  copied = false;

  aiService = inject(AIService);
 constructor(public modal: NgbActiveModal) {}



  // Form for ad message
  shareForm = new FormGroup({
    adMessage: new FormControl(''),
  });

  isGeneratingMessage: boolean = false;
  messageCharCount = 0;

  ngOnInit() {
    // Auto-generate ad message for admins on init

    this.generateAdMessageWithAI();

  }

  updateMessageCharCount() {
    const msg = this.adMessageControl.value || '';
    this.messageCharCount = msg.length;
  }

  generateAdMessageWithAI() {
    this.isGeneratingMessage = true;

    const prompt = `
You are an AI assistant creating advertising messages for both:
1. Nishwik Bank (a fintech bank offering property loans at the lowest EMI).
2. A real estate property listing.

Task:
- Create a short, engaging promotional message that highlights the property AND Nishwik Bank's low EMI financing.
- Keep it professional, appealing, and under 300 characters.
- Message should feel like a property ad + bank promo combined.

Property Information:
- Title: ${this.propertyData?.title || 'N/A'}
- Type: ${this.propertyData?.propertyType || 'N/A'}
- Listing Type: ${this.propertyData?.listingType || 'N/A'}
- Location: ${this.propertyData?.city || 'N/A'}, ${this.propertyData?.state || 'N/A'}
- Price: ₹${this.propertyData?.price || 'N/A'}
- BHK: ${this.propertyData?.bhk || 'N/A'}
- Area: ${this.propertyData?.superArea || 'N/A'} sq.ft
- Available: ${this.propertyData?.availableFrom || 'N/A'}

Output only the message.
    `;

    const data: AISearchQueryRequestDTO = { query: prompt };

    this.aiService.AIService_SearchQuery(data).subscribe({
      next: (res) => {
        this.shareForm.patchValue({ adMessage: typeof res === 'string' ? res.trim() : '' });
        this.isGeneratingMessage = false;
        this.updateMessageCharCount();
      },
      error: (err) => {
        console.error(err);
        this.isGeneratingMessage = false;
      },
    });
  }

  improveAdMessageWithAI() {
    this.isGeneratingMessage = true;

    const prompt = `
        You are an AI assistant refining advertising messages for both:
        1. Nishwik Bank (a fintech bank offering property loans at the lowest EMI).
        2. A real estate property listing.

        Task:
        - Improve the given message to be more catchy, professional, and appealing (under 300 characters).
        - Retain promotion of the property and Nishwik Bank's low EMI loans.

        Property Information:
        - Title: ${this.propertyData?.title || 'N/A'}
        - Type: ${this.propertyData?.propertyType || 'N/A'}
        - Listing Type: ${this.propertyData?.listingType || 'N/A'}
        - Location: ${this.propertyData?.city || 'N/A'}, ${this.propertyData?.state || 'N/A'}
        - Price: ₹${this.propertyData?.price || 'N/A'}
        - BHK: ${this.propertyData?.bhk || 'N/A'}
        - Area: ${this.propertyData?.superArea || 'N/A'} sq.ft
        - Available: ${this.propertyData?.availableFrom || 'N/A'}

        Current Message: ${this.adMessageControl.value || ''}

        Output only the improved message.
    `;

    const data: AISearchQueryRequestDTO = { query: prompt };

    this.aiService.AIService_SearchQuery(data).subscribe({
      next: (res) => {
        this.shareForm.patchValue({ adMessage: typeof res === 'string' ? res.trim() : '' });
        this.isGeneratingMessage = false;
        this.updateMessageCharCount();
      },
      error: (err) => {
        console.error(err);
        this.isGeneratingMessage = false;
      },
    });
  }

  get adMessageControl(): FormControl {
    return this.shareForm.get('adMessage') as FormControl;
  }

  openModal(type: string) {
    const shareText = this.adMessageControl.value ? `${this.adMessageControl.value} ${this.link}` : this.link;
    const encodedText = encodeURIComponent(shareText);

    switch (type) {
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodedText}`, '_blank');
        break;
      case 'email':
        window.open(`mailto:?subject=Check this Property from Nishwik&body=${encodedText}`, '_blank');
        break;
      case 'message':
        window.open(`sms:?body=${encodedText}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(this.link)}&quote=${encodeURIComponent(this.adMessageControl.value || '')}`, '_blank');
        break;
      case 'messenger':
        window.open(`fb-messenger://share?link=${encodeURIComponent(this.link)}&text=${encodeURIComponent(this.adMessageControl.value || '')}`, '_blank');
        break;
      case 'instagram':
        alert('Instagram does not allow direct web share. Copy link instead.');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodedText}`, '_blank');
        break;
      case 'embed':
        this.showEmbedModal();
        break;
    }
  }

  copyLink() {
    const textToCopy = this.adMessageControl.value ? `${this.adMessageControl.value}\n\n${this.link}` : this.link;
    navigator.clipboard.writeText(textToCopy).then(() => {
      this.copied = true;
      setTimeout(() => {
        this.copied = false;
      }, 2000);
    });
  }

  showEmbedModal() {
    const embedCode = `<iframe src="${this.link}" width="600" height="400"></iframe>`;
    alert(embedCode); // Replace with NgbModal if needed
  }

  @ViewChild('qrCode', { static: true }) qrCodeElem!: ElementRef;

  qrCode = new QRCodeStyling({
    width: 100,
    height: 100,
    type: "canvas",
    data: this.link,
    image: "assets/images/nishwik.png",
    dotsOptions: {
      type: "square",
      gradient: {
        type: "linear",
        rotation: Math.PI / 2,
        colorStops: [
          { offset: 0, color: "#011044" },
          { offset: 0.9, color: "#BA025E" },
          { offset: 1, color: "#F75629" }
        ]
      }
    },
    backgroundOptions: {
      color: "#ffffff"
    },
    imageOptions: {
      crossOrigin: "anonymous",
      margin: 4
    }
  });

  ngAfterViewInit() {
    this.qrCode.append(this.qrCodeElem.nativeElement);
    if (this.link) {
      this.qrCode.update({ data: this.link });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['link'] && this.link) {
      this.qrCode.update({ data: this.link });
    }
  }
}